import { create } from "zustand";
import { persist } from "zustand/middleware";

// Impersonation state — "View as" feature. Persisted to localStorage
// so the banner survives a page refresh. Cleared by the exit action.

export interface ImpersonationTarget {
  id: string;
  username: string;
  displayName: string;
  email: string | null;
  role: string;
  accountId: string | null;
  accountName: string | null;
  sfUserId: string | null;
  isSuperAdmin: boolean;
  permissions: string[];
}

interface ImpersonationState {
  sessionId: string | null;
  target: ImpersonationTarget | null;
  startedAt: string | null;       // ISO timestamp
  maxDurationSeconds: number;     // server-told cap (default 3600)

  start: (args: {
    sessionId: string;
    target: ImpersonationTarget;
    startedAt: string;
    maxDurationSeconds: number;
  }) => void;
  clear: () => void;
}

export const useImpersonationStore = create<ImpersonationState>()(
  persist(
    (set) => ({
      sessionId: null,
      target: null,
      startedAt: null,
      maxDurationSeconds: 3600,

      start: (args) => set({
        sessionId: args.sessionId,
        target: args.target,
        startedAt: args.startedAt,
        maxDurationSeconds: args.maxDurationSeconds,
      }),
      clear: () => set({
        sessionId: null,
        target: null,
        startedAt: null,
      }),
    }),
    {
      name: "millbank-impersonation",
      // Persist everything — we want the banner to survive refresh.
    },
  ),
);

/** True if a session is active AND hasn't expired client-side. The
 * server also enforces this, but checking here lets us auto-exit the
 * UI cleanly. */
export function isImpersonationActive(state?: ImpersonationState): boolean {
  const s = state ?? useImpersonationStore.getState();
  if (!s.target || !s.startedAt) return false;
  const startedMs = new Date(s.startedAt).getTime();
  const ageMs = Date.now() - startedMs;
  return ageMs < s.maxDurationSeconds * 1000;
}

/** True if a session WAS started but is now past expiry. The banner
 * uses this to switch to "Session expired — exit". */
export function isImpersonationExpired(state?: ImpersonationState): boolean {
  const s = state ?? useImpersonationStore.getState();
  if (!s.target || !s.startedAt) return false;
  const startedMs = new Date(s.startedAt).getTime();
  const ageMs = Date.now() - startedMs;
  return ageMs >= s.maxDurationSeconds * 1000;
}

/** Plain localStorage read of the current impersonation state — usable
 * from any module without causing circular imports. Returns null if no
 * session is active OR if it has expired. Used by getCurrentUser /
 * axios interceptor where importing the zustand store would create a
 * cycle (auth → session → store → api → auth). */
export function readImpersonationFromStorage(): {
  sessionId: string | null;
  target: ImpersonationTarget | null;
  startedAt: string | null;
  maxDurationSeconds: number;
} | null {
  try {
    const raw = localStorage.getItem("millbank-impersonation");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // zustand persist wraps state as { state: {...}, version: N }.
    const state = parsed?.state;
    if (!state || !state.target || !state.startedAt) return null;
    const ageMs = Date.now() - new Date(state.startedAt).getTime();
    if (ageMs >= (state.maxDurationSeconds || 3600) * 1000) return null;
    return state;
  } catch {
    return null;
  }
}
